/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

import { RESOLUTIONS, SlugSchema, type Resolution } from '@ips-device-verse/twin-service/schema/scene-doc';

// Re-exported because this is where the rest of the app looks for them, though
// the document schema is what defines them: a slug the agent may emit and a
// slug the importer may fetch have to be the same slug.
export { RESOLUTIONS, SlugSchema };
export type { Resolution };

/**
 * Poly Haven's public API, parsed.
 *
 * The browser calls it directly while editing (devplan §5.5): no proxy, no
 * mirror, no catalogue cache. Every response is parsed with a schema that keeps
 * only the fields we use, so a change upstream surfaces here rather than three
 * components deep — and an unexpected shape cannot reach the scene graph.
 */

const BASE = 'https://api.polyhaven.com';

export const ASSET_TYPES = ['models', 'hdris', 'textures'] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

/**
 * Nothing above 4k, though `max_resolution` often reads 8K or 16K: a 16K HDRI
 * is hundreds of megabytes and a scene has to load in a tab that is also
 * running the editor (devplan §5.4).
 */
export const DEFAULT_RESOLUTION: Resolution = '2k';

/**
 * Higher, because an HDRI is the only asset that fills the whole viewport: at
 * 2k the sky is a 2048x1024 image stretched across the screen, and its
 * compression blocks are plainly visible.
 */
export const DEFAULT_HDRI_RESOLUTION: Resolution = '4k';

const CatalogEntrySchema = z
  .object({
    name: z.string(),
    thumbnail_url: z.string().url(),
    files_hash: z.string().optional(),
    categories: z.array(z.string()).optional(),
    category: z.string().optional(),
    tags: z.array(z.string()).optional(),
    download_count: z.number().optional(),
    authors: z.record(z.string(), z.string()).optional(),
  })
  // Poly Haven adds fields; we keep the ones we render and ignore the rest.
  .loose();

export const CatalogSchema = z.record(z.string(), CatalogEntrySchema);

export interface CatalogAsset {
  slug: string;
  name: string;
  thumbnailUrl: string;
  filesHash?: string;
  categories: string[];
  downloads: number;
}

const SearchSchema = z.object({ results: z.array(z.object({ slug: z.string(), score: z.number() })) });

const FileSchema = z.object({ url: z.string().url(), size: z.number().optional(), md5: z.string().optional() });

const FileWithIncludesSchema = FileSchema.extend({ include: z.record(z.string(), FileSchema).optional() });

/** A texture map: resolution, then file format. */
const MapSchema = z.record(z.string(), z.record(z.string(), FileSchema));

/**
 * Only the shapes we consume, per devplan §5.4. `.loose()` because the
 * response also carries `blend`, `fbx`, `usd` and per-map entries we ignore.
 */
const FilesSchema = z
  .object({
    gltf: z.record(z.string(), z.object({ gltf: FileWithIncludesSchema })).optional(),
    hdri: z.record(z.string(), z.record(z.string(), FileSchema)).optional(),
    Diffuse: MapSchema.optional(),
    nor_gl: MapSchema.optional(),
    arm: MapSchema.optional(),
  })
  .loose();

export class PolyHavenError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'PolyHavenError';
  }
}

const request = async <T extends z.ZodType>(path: string, schema: T, signal?: AbortSignal): Promise<z.infer<T>> => {
  const response = await fetch(`${BASE}${path}`, { signal });

  if (!response.ok) {
    // 429 and 503 are documented and expected; the caller shows "unavailable"
    // rather than an empty grid, which would read as "we have nothing".
    throw new PolyHavenError(response.status, `Poly Haven answered ${response.status}.`);
  }

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new PolyHavenError(response.status, 'Poly Haven returned a response this client does not understand.');
  }
  return parsed.data;
};

const toCatalogAsset = (slug: string, entry: z.infer<typeof CatalogEntrySchema>): CatalogAsset => ({
  slug,
  name: entry.name,
  thumbnailUrl: entry.thumbnail_url,
  filesHash: entry.files_hash,
  categories: entry.categories ?? [],
  downloads: entry.download_count ?? 0,
});

/**
 * Every asset of a type, in one response.
 *
 * Poly Haven serves the whole catalogue at once, so the grid, the category
 * chips and the offline fallback all come from a single call rather than a
 * request per keystroke (devplan §5.6).
 */
export const fetchCatalog = async (type: AssetType, signal?: AbortSignal): Promise<CatalogAsset[]> => {
  const catalog = await request(`/assets?t=${type}`, CatalogSchema, signal);

  return Object.entries(catalog)
    .map(([slug, entry]) => toCatalogAsset(slug, entry))
    .sort((a, b) => b.downloads - a.downloads);
};

/** Semantic and multilingual, which is what makes a Japanese search box work. */
export const searchSlugs = async (
  query: string,
  type: AssetType,
  limit: number,
  signal?: AbortSignal
): Promise<string[]> => {
  const params = new URLSearchParams({ q: query.trim().toLowerCase(), t: type, limit: String(limit) });
  const { results } = await request(`/search?${params.toString()}`, SearchSchema, signal);
  return results.map((result) => result.slug);
};

export interface GltfFiles {
  url: string;
  /** Keyed by the path the `.gltf` refers to, which is *not* where the file lives. */
  include: Record<string, string>;
}

/**
 * Where a model's files actually are.
 *
 * The `.gltf` names its buffer and images by relative path, but Poly Haven
 * serves them under a different prefix — so resolving them relatively 404s.
 * The include map is the only way to find them, which is also why it can be
 * the importer's allowlist (devplan §5.5).
 */
export const fetchGltf = async (
  slug: string,
  resolution: Resolution,
  signal?: AbortSignal
): Promise<GltfFiles | undefined> => {
  const files = await request(`/files/${SlugSchema.parse(slug)}`, FilesSchema, signal);
  const entry = files.gltf?.[resolution]?.gltf;

  if (!entry) {
    return undefined;
  }
  return {
    url: entry.url,
    include: Object.fromEntries(Object.entries(entry.include ?? {}).map(([path, file]) => [path, file.url])),
  };
};

export const fetchHdr = async (
  slug: string,
  resolution: Resolution,
  signal?: AbortSignal
): Promise<string | undefined> => {
  const files = await request(`/files/${SlugSchema.parse(slug)}`, FilesSchema, signal);
  return files.hdri?.[resolution]?.hdr?.url;
};

export interface TextureMaps {
  albedo?: string;
  /** OpenGL convention, which is also Babylon's. */
  normal?: string;
  /** Ambient occlusion, roughness and metalness in R, G and B. */
  arm?: string;
}

/**
 * A texture set, as the three maps a PBR material needs.
 *
 * `arm` rather than `AO` + `Rough` + `Metal`: Poly Haven packs all three into
 * one image and Babylon reads it in that layout, so it is one download instead
 * of three. JPEG rather than PNG — the PNGs are eight times the size for a
 * difference nobody sees on a wall.
 */
export const fetchTexture = async (
  slug: string,
  resolution: Resolution,
  signal?: AbortSignal
): Promise<TextureMaps> => {
  const files = await request(`/files/${SlugSchema.parse(slug)}`, FilesSchema, signal);

  return {
    albedo: files.Diffuse?.[resolution]?.jpg?.url,
    normal: files.nor_gl?.[resolution]?.jpg?.url,
    arm: files.arm?.[resolution]?.jpg?.url,
  };
};
